import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, FolderKanban, Plus, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../use-debounced-value';
import {
  buildWikiTree,
  listWikiPages,
  listWikiSpaces,
  parseWikiLocation,
  wikiRoute,
  type WikiPageSlim,
  type WikiSpaceDto,
  type WikiSpaceRef,
} from '../../lib/wiki';
import { sidebarIconClass, sidebarItemClass } from '../work/nav-item';
import WikiPageTree from './WikiPageTree';

type Props = {
  onNavigate?: () => void;
};

type WikiHit = { space: WikiSpaceRef; spaceName: string; page: WikiPageSlim };

const SEARCH_DEBOUNCE_MS = 200;

function spaceRefOf(dto: WikiSpaceDto): WikiSpaceRef | null {
  if (dto.kind === 'org') return { kind: 'org', projectId: null };
  if (typeof dto.id !== 'string' || dto.id.length === 0) return null;
  return { kind: 'project', projectId: dto.id };
}

/**
 * Nested under the Wiki entry in the app sidebar: a search across every space the user can
 * see, then each space with Create and the page tree on the open one.
 */
export default function WikiSidebarNav({ onNavigate }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const current = useMemo(() => parseWikiLocation(location.pathname), [location.pathname]);
  const [spaces, setSpaces] = useState<WikiSpaceDto[]>([]);
  const [pages, setPages] = useState<WikiPageSlim[]>([]);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<WikiHit[]>([]);
  const [searching, setSearching] = useState(false);
  const needle = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    let cancelled = false;
    listWikiSpaces()
      .then((items) => {
        if (!cancelled) setSpaces(items);
      })
      .catch(() => {
        if (!cancelled) setSpaces([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const spaceKey = current ? `${current.space.kind}:${current.space.projectId ?? ''}` : null;

  // Every navigation inside the wiki (create, move, delete, update) reloads the tree.
  useEffect(() => {
    if (!current) {
      setPages([]);
      return;
    }
    let cancelled = false;
    listWikiPages(current.space)
      .then((rows) => {
        if (!cancelled) setPages(rows);
      })
      .catch(() => {
        if (!cancelled) setPages([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceKey, location.key]);

  useEffect(() => {
    if (needle.length === 0) {
      setHits([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const targets = spaces
      .map((dto) => {
        const space = spaceRefOf(dto);
        return space ? { space, spaceName: dto.name } : null;
      })
      .filter((row): row is { space: WikiSpaceRef; spaceName: string } => row !== null);
    void Promise.all(
      targets.map(({ space, spaceName }) =>
        listWikiPages(space, needle)
          .then((rows) => rows.map((page): WikiHit => ({ space, spaceName, page })))
          .catch((): WikiHit[] => []),
      ),
    ).then((groups) => {
      if (cancelled) return;
      const merged = groups.flat().sort((a, b) => {
        const byTitle = a.page.title.localeCompare(b.page.title, undefined, { sensitivity: 'base' });
        if (byTitle !== 0) return byTitle;
        return a.spaceName.localeCompare(b.spaceName, undefined, { sensitivity: 'base' });
      });
      setHits(merged);
      setSearching(false);
    });
    return () => {
      cancelled = true;
    };
  }, [needle, spaces]);

  const byId = useMemo(() => new Map(pages.map((page) => [page.id, page])), [pages]);
  const tree = useMemo(() => buildWikiTree(pages), [pages]);
  const selected = current?.path ? pages.find((page) => page.path === current.path) ?? null : null;
  const expand = useMemo(() => {
    const ids = new Set<string>();
    let cursor = selected;
    while (cursor?.parent_id) {
      ids.add(cursor.parent_id);
      cursor = byId.get(cursor.parent_id) ?? null;
    }
    return ids;
  }, [selected, byId]);
  const typed = query.trim();
  const showResults = typed.length > 0;
  const pending = showResults && (typed !== needle || searching);

  function open(space: WikiSpaceRef, page: WikiPageSlim) {
    navigate(wikiRoute(space, page.path));
    onNavigate?.();
  }

  function create() {
    // The space page owns the dialog; `?create=1` opens it under the page being viewed.
    const params = new URLSearchParams(location.search);
    params.set('create', '1');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  }

  if (spaces.length === 0) return null;

  return (
    <div className="ml-4 space-y-px border-l pl-1.5" aria-label="Wiki spaces">
      <div className="relative py-1">
        <Search className="pointer-events-none absolute left-2 top-3 h-3 w-3 text-muted-foreground" aria-hidden />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search wiki…"
          className="h-7 pl-6 text-xs"
          aria-label="Search all wiki pages"
        />
      </div>
      {showResults ? (
        pending ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">Searching…</p>
        ) : hits.length === 0 ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">No pages match.</p>
        ) : (
          <ul className="space-y-px pb-1">
            {hits.map((hit) => (
              <li key={hit.page.id}>
                <Link
                  to={wikiRoute(hit.space, hit.page.path)}
                  onClick={onNavigate}
                  className={cn(
                    'block w-full rounded-md px-2 py-1 text-left text-sm hover:bg-accent/70',
                    hit.page.id === selected?.id && 'bg-primary/10 font-medium text-primary',
                  )}
                >
                  <span className="block truncate">{hit.page.title}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {hit.spaceName} · {hit.page.path}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
      {spaces.map((dto) => {
        const space = spaceRefOf(dto);
        if (!space) return null;
        const route = wikiRoute(space);
        const active = location.pathname === route || location.pathname.startsWith(`${route}/`);
        const Icon = dto.kind === 'org' ? Building2 : FolderKanban;
        return (
          <div key={`${dto.kind}:${dto.id ?? 'org'}`}>
            <Link
              to={route}
              onClick={onNavigate}
              className={cn(sidebarItemClass(active), 'py-1')}
              aria-current={active ? 'page' : undefined}
              title={dto.name}
            >
              <Icon className={sidebarIconClass(active)} aria-hidden />
              <span className="truncate">{dto.name}</span>
            </Link>
            {active && current ? (
              <div className="ml-2 space-y-1 py-1">
                <button type="button" className={cn(sidebarItemClass(false), 'py-1')} onClick={create}>
                  <Plus className={sidebarIconClass(false)} aria-hidden />
                  Create
                </button>
                {showResults ? null : pages.length === 0 ? (
                  <p className="px-2 py-1 text-xs text-muted-foreground">No pages yet.</p>
                ) : (
                  <WikiPageTree
                    nodes={tree}
                    selectedId={selected?.id ?? null}
                    expandPathTo={expand}
                    onSelect={(id) => {
                      const page = byId.get(id);
                      if (page) open(current.space, page);
                    }}
                  />
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
